import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Rocket, TrendingUp, Zap } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

type Period = 'monthly' | 'annual';

const Upgrade = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState<string>('start');
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('annual');
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      toast({ title: 'Pagamento recebido!', description: 'Seu plano será ativado em instantes.' });
    } else if (status === 'cancel') {
      toast({ title: 'Checkout cancelado', description: 'Você pode tentar novamente quando quiser.' });
    }
  }, [searchParams]);

  const formatBRL = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const plans = [
    {
      name: 'Start',
      value: 'start',
      priceMonthly: 0,
      priceAnnual: 0,
      description: 'Perfeito para começar',
      features: [
        'Apenas o coordenador',
        'Até 10 personas',
        'Sem histórico',
        '10 feedbacks/mês (nota > 50)',
      ],
      icon: Rocket,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      name: 'Expert',
      value: 'expert',
      priceMonthly: 4485,   // R$ 44,85 / mês
      priceAnnual: 2990,    // R$ 29,90 / mês (cobrado anualmente)
      annualTotal: 35880,   // R$ 358,80 / ano
      description: 'Para equipes em crescimento',
      features: [
        'Até 5 vendedores + coordenador',
        'Personas ilimitadas',
        'Histórico liberado',
        '100 feedbacks/mês (nota > 50)',
        'Feedbacks avulsos: R$0,30/unidade',
      ],
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-500',
      popular: true,
    },
    {
      name: 'Max Pro',
      value: 'max_pro',
      priceMonthly: 2985,   // R$ 29,85 / mês / usuário
      priceAnnual: 1990,    // R$ 19,90 / mês / usuário (cobrado anualmente)
      annualTotal: 23880,   // R$ 238,80 / ano / usuário
      perUser: true,
      description: 'Para empresas estabelecidas',
      features: [
        'Vendedores ilimitados',
        'Histórico liberado',
        'Personas ilimitadas',
        'Feedbacks ilimitados',
      ],
      icon: Zap,
      color: 'from-orange-500 to-red-500',
    },
  ];

  useEffect(() => {
    const loadCurrentPlan = async () => {
      if (authLoading) return;
      if (!user) { navigate('/auth'); return; }

      try {
        const { data: usuario } = await supabase
          .from('usuarios')
          .select('papel, empresa_id')
          .eq('id', user.id)
          .single();

        if (!usuario || usuario.papel !== 'coordenador') {
          navigate('/dashboard');
          return;
        }

        if (usuario.empresa_id) {
          setEmpresaId(usuario.empresa_id);
          const { data: empresaData } = await supabase
            .from('empresas')
            .select('plano')
            .eq('id', usuario.empresa_id)
            .single();
          if (empresaData) setCurrentPlan(empresaData.plano || 'start');
        }
      } catch (error) {
        console.error('Error loading plan:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCurrentPlan();
  }, [user, authLoading, navigate]);

  const handleUpgrade = async (planValue: string) => {
    if (!empresaId) return;
    if (planValue === 'start') {
      toast({ title: 'Plano gratuito', description: 'Este é o plano gratuito.' });
      return;
    }
    if (planValue === currentPlan) {
      toast({ title: 'Plano atual', description: 'Você já está neste plano.' });
      return;
    }

    setProcessing(planValue);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan: planValue, period },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de checkout não retornada.');
      }
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Não foi possível iniciar o checkout', description: e?.message ?? 'Tente novamente.', variant: 'destructive' });
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Layout title="Upgrade de Plano">
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Escolha o melhor plano para você
          </h1>
          <p className="text-muted-foreground text-lg">
            Escale seu negócio com mais recursos e feedbacks
          </p>
        </div>

        {/* Toggle Mensal / Anual */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-muted rounded-full p-1">
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition ${
                period === 'monthly' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setPeriod('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
                period === 'annual' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              Anual
              <Badge variant="secondary" className="ml-1">-33%</Badge>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrent = plan.value === currentPlan;
            const cents = period === 'monthly' ? plan.priceMonthly : plan.priceAnnual;
            const isFree = plan.value === 'start';
            const isProcessing = processing === plan.value;

            return (
              <Card
                key={plan.value}
                className={`relative ${plan.popular ? 'border-2 border-primary shadow-lg scale-105' : ''} ${isCurrent ? 'opacity-90' : ''}`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Mais Popular</Badge>
                )}
                {isCurrent && (
                  <Badge variant="secondary" className="absolute -top-3 right-4">Plano Atual</Badge>
                )}
                <CardHeader>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    {isFree ? (
                      <span className="text-3xl font-bold">Grátis</span>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold">{formatBRL(cents)}</span>
                          <span className="text-sm text-muted-foreground">
                            /mês{plan.perUser ? ' /usuário' : ''}
                          </span>
                        </div>
                        {period === 'annual' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Cobrado anualmente ({formatBRL(plan.annualTotal!)}{plan.perUser ? ' /usuário' : ''})
                          </p>
                        )}
                        {period === 'monthly' && (
                          <p className="text-xs text-muted-foreground mt-1">Cobrança mensal</p>
                        )}
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleUpgrade(plan.value)}
                    disabled={isCurrent || isProcessing || isFree}
                    variant={plan.popular ? 'default' : 'outline'}
                    className="w-full"
                  >
                    {isProcessing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecionando...</>
                    ) : isCurrent ? 'Plano Atual' : isFree ? 'Plano Gratuito' : 'Assinar'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Pagamento processado com segurança pela Stripe. Você pode cancelar a qualquer momento.
        </p>
      </div>
    </Layout>
  );
};

export default Upgrade;
